import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Book, Video, FileText, Download, Code, AlertCircle, User, LogOut, Upload, Edit, Trash2, Search, Filter, RefreshCw } from 'lucide-react';

// Configuration
const CONFIG = {
  gasWebAppUrl: 'https://script.google.com/macros/s/YOUR_GAS_SCRIPT_ID/exec', // Placeholder for Google Apps Script URL
  googleClientId: 'YOUR_GOOGLE_CLIENT_ID', // Placeholder for Google Client ID
  semesters: [
    { id: 1, name: '1st Semester' },
    { id: 2, name: '2nd Semester' },
    { id: 3, name: '3rd Semester' },
    { id: 4, name: '4th Semester' },
    { id: 5, name: '5th Semester' },
    { id: 6, name: '6th Semester' },
    { id: 7, name: '7th Semester' },
    { id: 8, name: '8th Semester' }
  ],
  contentCategories: [
    { id: 'notes', name: 'Notes', icon: FileText },
    { id: 'books', name: 'Books', icon: Book },
    { id: 'videos', name: 'Video References', icon: Video },
    { id: 'assignments', name: 'Assignments', icon: Edit },
    { id: 'pyqs', name: 'Previous Year Questions', icon: FileText },
    { id: 'pdfs', name: 'PDFs', icon: Download },
    { id: 'software', name: 'Software Links', icon: Code },
    { id: 'special', name: 'Special', icon: AlertCircle }
  ]
};

// Utility functions
const getEmbedUrl = (driveLink) => {
  if (!driveLink) return null;
  // Regex to extract file ID from various Google Drive share links
  const match = driveLink.match(/(?:id=)?([a-zA-Z0-9_-]{28,})/);
  if (match && match[1]) {
    // For documents, sheets, slides, etc., use /preview
    // For general files, use /view
    // This is a simplified approach, actual embedding might require more specific handling
    return `https://docs.google.com/document/d/${match[1]}/preview`;
  }
  return driveLink; // Return original if no match, though it might not embed correctly
};

const getYouTubeEmbedUrl = (youtubeUrl) => {
  if (!youtubeUrl) return null;
  const match = youtubeUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (match && match[1]) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }
  return youtubeUrl;
};

// Loading Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-8">
    <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
  </div>
);

// Error Component
const ErrorMessage = ({ message, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
    <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
    <p className="text-red-700 mb-3">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
      >
        Try Again
      </button>
    )}
  </div>
);

// Success Message Component
const SuccessMessage = ({ message, onClose }) => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
    <div className="flex items-center justify-center mb-2">
      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
        <span className="text-white text-sm">✓</span>
      </div>
    </div>
    <p className="text-green-700 mb-3">{message}</p>
    <button
      onClick={onClose}
      className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
    >
      Close
    </button>
  </div>
);

// Confirmation Modal Component (Replaces window.confirm)
const ConfirmationModal = ({ message, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-lg">
        <p className="text-lg font-semibold text-gray-800 mb-6 text-center">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};


// Content Display Component
const ContentDisplay = ({ content, category, isAdmin, onEdit, onDelete }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const filteredContent = content.filter(item => {
    const matchesSearch = !searchTerm ||
      (item.subject && item.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.unit && item.unit.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesSubject = selectedSubject === 'all' ||
      (item.subject && item.subject === selectedSubject);

    return matchesSearch && matchesSubject;
  });

  const subjects = [...new Set(content.map(item => item.subject).filter(Boolean))];

  if (content.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>No content available for this category yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {subjects.length > 0 && (
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Subjects</option>
              {subjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Content Items */}
      {filteredContent.map((item, index) => (
        <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleExpanded(index)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">
                  {item.title || `${item.subject} - ${item.unit}`}
                </h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  {item.subject && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {item.subject}
                    </span>
                  )}
                  {item.unit && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      {item.unit}
                    </span>
                  )}
                  {item.subunit && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                      {item.subunit}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                      className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-full hover:bg-blue-100"
                      title="Edit content"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(index); }}
                      className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-full hover:bg-red-100"
                      title="Delete content"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                {expandedItems.has(index) ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </div>

          {expandedItems.has(index) && (
            <div className="border-t border-gray-200 p-4">
              {category === 'videos' && item.youtubeLink ? (
                <div className="aspect-video">
                  <iframe
                    src={getYouTubeEmbedUrl(item.youtubeLink)}
                    className="w-full h-full rounded-lg"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={item.title || "YouTube video"}
                  />
                </div>
              ) : item.driveLink ? (
                <div className="space-y-4">
                  <div className="h-96 border border-gray-200 rounded-lg">
                    <iframe
                      src={getEmbedUrl(item.driveLink)}
                      className="w-full h-full rounded-lg"
                      frameBorder="0"
                      title={item.title || "Google Drive document"}
                    />
                  </div>
                  <a
                    href={item.driveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Open in Google Drive
                  </a>
                </div>
              ) : (
                <p className="text-gray-500">No preview available</p>
              )}
            </div>
          )}
        </div>
      ))}

      {filteredContent.length === 0 && content.length > 0 && (
        <div className="text-center py-8 text-gray-500">
          <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No content matches your search criteria.</p>
        </div>
      )}
    </div>
  );
};

// Grievance Form Component
const GrievanceForm = ({ currentSemester, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    semester: currentSemester,
    category: 'Content Error',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      setFormData({
        name: '',
        email: '',
        semester: currentSemester,
        category: 'Content Error',
        description: ''
      });
    } catch (error) {
      console.error('Error submitting grievance:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Submit Grievance</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="grievance-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name (Optional)
            </label>
            <input
              type="text"
              id="grievance-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="grievance-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email (Optional)
            </label>
            <input
              type="email"
              id="grievance-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="grievance-semester" className="block text-sm font-medium text-gray-700 mb-1">
              Semester
            </label>
            <select
              id="grievance-semester"
              name="semester"
              value={formData.semester}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {CONFIG.semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="grievance-category" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="grievance-category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Content Error">Content Error</option>
              <option value="Broken Link">Broken Link</option>
              <option value="Suggestion">Suggestion</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="grievance-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="grievance-description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Please describe your grievance in detail..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Admin Content Form Component
const AdminContentForm = ({ category, onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState({
    semester: initialData?.semester || '',
    subject: initialData?.subject || '',
    unit: initialData?.unit || '',
    subunit: initialData?.subunit || '',
    title: initialData?.title || '',
    description: initialData?.description || '',
    driveLink: initialData?.driveLink || '',
    youtubeLink: initialData?.youtubeLink || '',
    author: initialData?.author || '',
    year: initialData?.year || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {initialData ? 'Edit' : 'Add'} {category} Content
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="admin-semester" className="block text-sm font-medium text-gray-700 mb-1">
                Semester *
              </label>
              <select
                id="admin-semester"
                name="semester"
                value={formData.semester}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Semester</option>
                {CONFIG.semesters.map(sem => (
                  <option key={sem.id} value={sem.id}>{sem.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="admin-subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject *
              </label>
              <input
                type="text"
                id="admin-subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="admin-unit" className="block text-sm font-medium text-gray-700 mb-1">
                Unit
              </label>
              <input
                type="text"
                id="admin-unit"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="admin-subunit" className="block text-sm font-medium text-gray-700 mb-1">
                Subunit
              </label>
              <input
                type="text"
                id="admin-subunit"
                name="subunit"
                value={formData.subunit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label htmlFor="admin-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              id="admin-title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {category === 'books' && (
            <div>
              <label htmlFor="admin-author" className="block text-sm font-medium text-gray-700 mb-1">
                Author
              </label>
              <input
                type="text"
                id="admin-author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {category === 'pyqs' && (
            <div>
              <label htmlFor="admin-year" className="block text-sm font-medium text-gray-700 mb-1">
                Year/Exam Details
              </label>
              <input
                type="text"
                id="admin-year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label htmlFor="admin-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="admin-description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {category === 'videos' ? (
            <div>
              <label htmlFor="admin-youtube-link" className="block text-sm font-medium text-gray-700 mb-1">
                YouTube Link *
              </label>
              <input
                type="url"
                id="admin-youtube-link"
                name="youtubeLink"
                value={formData.youtubeLink}
                onChange={handleChange}
                required
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          ) : (
            <div>
              <label htmlFor="admin-drive-link" className="block text-sm font-medium text-gray-700 mb-1">
                Google Drive Link *
              </label>
              <input
                type="url"
                id="admin-drive-link"
                name="driveLink"
                value={formData.driveLink}
                onChange={handleChange}
                required
                placeholder="https://drive.google.com/file/d/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [currentSemester, setCurrentSemester] = useState(1);
  const [currentCategory, setCurrentCategory] = useState('notes');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGrievanceForm, setShowGrievanceForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contentToDeleteIndex, setContentToDeleteIndex] = useState(null);

  // Simulate API calls (replace with actual GAS integration)
  const fetchContent = async (semester, category) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock data
      const mockContent = {
        notes: [
          {
            subject: 'Data Structures',
            unit: 'Unit 1',
            subunit: 'Arrays and Linked Lists',
            driveLink: 'https://docs.google.com/document/d/1234567890_mock_id/edit',
            title: 'Introduction to Data Structures'
          },
          {
            subject: 'Algorithms',
            unit: 'Unit 2',
            subunit: 'Sorting Algorithms',
            driveLink: 'https://docs.google.com/document/d/0987654321_mock_id/edit',
            title: 'Sorting Techniques'
          }
        ],
        videos: [
          {
            subject: 'Data Structures',
            unit: 'Unit 1',
            subunit: 'Arrays',
            youtubeLink: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            title: 'Array Operations Explained'
          }
        ],
        books: [
          {
            subject: 'Data Structures',
            title: 'Introduction to Algorithms',
            author: 'Thomas H. Cormen',
            driveLink: 'https://docs.google.com/document/d/1111111111_mock_id/edit'
          }
        ],
        assignments: [],
        pyqs: [],
        pdfs: [],
        software: [],
        special: []
      };

      setContent(prevContent => ({
        ...prevContent,
        [`${semester}-${category}`]: mockContent[category] || []
      }));
    } catch (err) {
      setError('Failed to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitGrievance = async (grievanceData) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccessMessage('Grievance submitted successfully!');
      setShowGrievanceForm(false);

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to submit grievance. Please try again.');
    }
  };

  const handleLogin = () => {
    // Simulate Google login
    setIsLoggedIn(true);
    setUser({ email: 'admin@example.com', name: 'Admin User' });
    setIsAdmin(true);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    setIsAdmin(false);
    setCurrentPage('home');
  };

  const handleContentSubmit = async (formData) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const contentKey = `${formData.semester}-${currentCategory}`;
      const newContent = {
        ...formData,
        timestamp: new Date().toISOString(),
        uploadedBy: user?.email || 'admin'
      };

      if (editingContent) {
        // Update existing content
        const updatedContent = [...(content[contentKey] || [])];
        const editIndex = updatedContent.findIndex(item =>
          item.subject === editingContent.subject &&
          item.unit === editingContent.unit &&
          item.subunit === editingContent.subunit &&
          item.title === editingContent.title // Added title for more robust identification
        );

        if (editIndex !== -1) {
          updatedContent[editIndex] = newContent;
        }

        setContent(prevContent => ({
          ...prevContent,
          [contentKey]: updatedContent
        }));

        setSuccessMessage('Content updated successfully!');
      } else {
        // Add new content
        setContent(prevContent => ({
          ...prevContent,
          [contentKey]: [...(prevContent[contentKey] || []), newContent]
        }));

        setSuccessMessage('Content added successfully!');
      }

      setShowAdminForm(false);
      setEditingContent(null);

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to save content. Please try again.');
    }
  };

  const handleDeleteContent = (index) => {
    setContentToDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false); // Close the confirmation modal
    if (contentToDeleteIndex !== null) {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));

        // Update the content state by removing the item at the specified index
        const contentKey = `${currentSemester}-${currentCategory}`;
        const updatedContent = [...(content[contentKey] || [])];
        updatedContent.splice(contentToDeleteIndex, 1);

        setContent(prevContent => ({
          ...prevContent,
          [contentKey]: updatedContent
        }));

        setSuccessMessage('Content deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        setError('Failed to delete content. Please try again.');
      } finally {
        setContentToDeleteIndex(null); // Reset index
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setContentToDeleteIndex(null);
  };


  useEffect(() => {
    if (currentPage === 'semester' || (currentPage === 'admin' && isAdmin)) {
      fetchContent(currentSemester, currentCategory);
    }
  }, [currentPage, currentSemester, currentCategory, isAdmin]);

  const currentContent = content[`${currentSemester}-${currentCategory}`] || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
              >
                CS Department
              </button>
            </div>

            <div className="flex items-center gap-4">
              {currentPage !== 'home' && (
                <button
                  onClick={() => setCurrentPage('home')}
                  className="text-gray-600 hover:text-gray-900 transition-colors hidden sm:block"
                >
                  Home
                </button>
              )}

              {isLoggedIn ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700 hidden sm:inline">{user?.name}</span>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => setCurrentPage('admin')}
                      className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                    >
                      Admin
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-100"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLogin}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50">
          <SuccessMessage
            message={successMessage}
            onClose={() => setSuccessMessage('')}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Home Page */}
        {currentPage === 'home' && (
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Computer Science Department
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Your comprehensive resource for CS academic materials
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {CONFIG.semesters.map(semester => (
                <button
                  key={semester.id}
                  onClick={() => {
                    setCurrentSemester(semester.id);
                    setCurrentPage('semester');
                  }}
                  className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {semester.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Access all materials
                      </p>
                    </div>
                    <ChevronDown className="w-5 h-5 text-gray-400 transform rotate-[-90deg]" />
                  </div>
                </button>
              ))}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Helpful Codes & Extra Reference
              </h2>
              <p className="text-gray-600 mb-4">
                Find additional resources, code snippets, and external references not tied to specific semesters.
              </p>
              <button
                onClick={() => setCurrentPage('special')}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors inline-flex items-center gap-2"
              >
                <Code className="w-4 h-4" />
                Browse Resources
              </button>
            </div>
          </div>
        )}

        {/* Semester Page */}
        {currentPage === 'semester' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">
                {CONFIG.semesters.find(s => s.id === currentSemester)?.name}
              </h1>
              <button
                onClick={() => setShowGrievanceForm(true)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors inline-flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                Report Issue
              </button>
            </div>

            {/* Category Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 overflow-x-auto">
                {CONFIG.contentCategories.map(category => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setCurrentCategory(category.id)}
                      className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                        currentCategory === category.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {category.name}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Content Display */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {error ? (
                <ErrorMessage
                  message={error}
                  onRetry={() => fetchContent(currentSemester, currentCategory)}
                />
              ) : loading ? (
                <LoadingSpinner />
              ) : (
                <ContentDisplay
                  content={currentContent}
                  category={currentCategory}
                  isAdmin={isAdmin} // Pass isAdmin prop
                  onEdit={(item) => {
                    setEditingContent(item);
                    setShowAdminForm(true);
                  }}
                  onDelete={handleDeleteContent} // Pass the new handler
                />
              )}
            </div>
          </div>
        )}

        {/* Admin Page */}
        {currentPage === 'admin' && isAdmin && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <button
                onClick={() => {
                  setEditingContent(null); // Ensure adding new content
                  setShowAdminForm(true);
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Add Content
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Content</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {Object.values(content).flat().length}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Semesters</h3>
                <p className="text-3xl font-bold text-green-600">
                  {CONFIG.semesters.length}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Categories</h3>
                <p className="text-3xl font-bold text-purple-600">
                  {CONFIG.contentCategories.length}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Status</h3>
                <p className="text-sm font-medium text-green-600">Active</p>
              </div>
            </div>

            {/* Content Management */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200 p-4">
                <h2 className="text-xl font-bold text-gray-900">Content Management</h2>
              </div>
              <div className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <select
                    value={currentSemester}
                    onChange={(e) => setCurrentSemester(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {CONFIG.semesters.map(sem => (
                      <option key={sem.id} value={sem.id}>{sem.name}</option>
                    ))}
                  </select>
                  <select
                    value={currentCategory}
                    onChange={(e) => setCurrentCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {CONFIG.contentCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {currentContent.length > 0 ? (
                  <div className="space-y-4">
                    {currentContent.map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">
                              {item.title || `${item.subject} - ${item.unit}`}
                            </h3>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {item.subject && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  {item.subject}
                                </span>
                              )}
                              {item.unit && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                  {item.unit}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                setEditingContent(item);
                                setShowAdminForm(true);
                              }}
                              className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-full hover:bg-blue-100"
                              title="Edit content"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteContent(index)}
                              className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-full hover:bg-red-100"
                              title="Delete content"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No content available for this category.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Special/Helpful Codes Page */}
        {currentPage === 'special' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">
                Helpful Codes & Extra Reference
              </h1>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-center py-8 text-gray-500">
                <Code className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Special resources will be displayed here.</p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Grievance Form Modal */}
      {showGrievanceForm && (
        <GrievanceForm
          currentSemester={currentSemester}
          onSubmit={submitGrievance}
          onClose={() => setShowGrievanceForm(false)}
        />
      )}

      {/* Admin Content Form Modal */}
      {showAdminForm && (
        <AdminContentForm
          category={currentCategory}
          onSubmit={handleContentSubmit}
          onCancel={() => {
            setShowAdminForm(false);
            setEditingContent(null);
          }}
          initialData={editingContent}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmationModal
          message="Are you sure you want to delete this content? This action cannot be undone."
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default App;
